import * as CSS from 'csstype';
import { ResponsiveValue, TLengthStyledSystem, PositionProps, Config, system, compose } from 'styled-system';
import { ColorType } from '../../theme/colors';

const POPOVER_GAP_SIZE = '16px';
const POPOVER_ARROW_SIZE = 6;
const POPOVER_POSITION = `calc(100% + ${POPOVER_GAP_SIZE})`;
export const POPOVER_BACKGROUND_COLOR: ColorType = 'serenade';
export const POPOVER_BORDER_COLOR: ColorType = 'serenade';
export const POPOVER_ARROW_BORDER_WIDTH_INNER = `${POPOVER_ARROW_SIZE}px`;
export const POPOVER_ARROW_BORDER_WIDTH_OUTER = `${POPOVER_ARROW_SIZE + 2}px`;

export enum PopoverPosition {
  Top = 'top',
  Right = 'right',
  Bottom = 'bottom',
  Left = 'left',
}

export interface ArrowProps {
  arrowPositionTop?: ResponsiveValue<CSS.Property.Top<TLengthStyledSystem>>;
  arrowPositionRight?: ResponsiveValue<CSS.Property.Right<TLengthStyledSystem>>;
  arrowPositionBottom?: ResponsiveValue<CSS.Property.Bottom<TLengthStyledSystem>>;
  arrowPositionLeft?: ResponsiveValue<CSS.Property.Left<TLengthStyledSystem>>;
  arrowInnerBorderTopColor?: ResponsiveValue<CSS.Property.BorderTopColor>;
  arrowInnerBorderRightColor?: ResponsiveValue<CSS.Property.BorderRightColor>;
  arrowInnerBorderBottomColor?: ResponsiveValue<CSS.Property.BorderBottomColor>;
  arrowInnerBorderLeftColor?: ResponsiveValue<CSS.Property.BorderLeftColor>;
  arrowInnerMarginLeft?: ResponsiveValue<CSS.Property.MarginLeft<TLengthStyledSystem>>;
  arrowInnerMarginTop?: ResponsiveValue<CSS.Property.MarginTop<TLengthStyledSystem>>;
  arrowOuterBorderTopColor?: ResponsiveValue<CSS.Property.BorderTopColor>;
  arrowOuterBorderRightColor?: ResponsiveValue<CSS.Property.BorderRightColor>;
  arrowOuterBorderBottomColor?: ResponsiveValue<CSS.Property.BorderBottomColor>;
  arrowOuterBorderLeftColor?: ResponsiveValue<CSS.Property.BorderLeftColor>;
  arrowOuterMarginLeft?: ResponsiveValue<CSS.Property.MarginLeft<TLengthStyledSystem>>;
  arrowOuterMarginTop?: ResponsiveValue<CSS.Property.MarginTop<TLengthStyledSystem>>;
}

export interface PopoverPositionProps extends PositionProps {
  transform?: ResponsiveValue<CSS.Property.Transform>;
}

interface PositionData {
  popoverPosition: PopoverPositionProps;
  arrowProps: ArrowProps;
}

export const popoverPositionMap: {
  [key in PopoverPosition]: PositionData;
} = {
  [PopoverPosition.Top]: {
    popoverPosition: {
      bottom: POPOVER_POSITION,
    },
    arrowProps: {
      arrowPositionTop: '100%',
      arrowPositionLeft: '50%',
      arrowInnerBorderTopColor: POPOVER_BACKGROUND_COLOR,
      arrowInnerMarginLeft: `-${POPOVER_ARROW_BORDER_WIDTH_INNER}`,
      arrowOuterBorderTopColor: POPOVER_BORDER_COLOR,
      arrowOuterMarginLeft: `-${POPOVER_ARROW_BORDER_WIDTH_OUTER}`,
    },
  },
  [PopoverPosition.Right]: {
    popoverPosition: {
      left: POPOVER_POSITION,
      top: '50%',
      transform: 'translateY(-50%)',
    },
    arrowProps: {
      arrowPositionTop: '50%',
      arrowPositionRight: '100%',
      arrowInnerBorderRightColor: POPOVER_BACKGROUND_COLOR,
      arrowInnerMarginTop: `-${POPOVER_ARROW_BORDER_WIDTH_INNER}`,
      arrowOuterBorderRightColor: POPOVER_BORDER_COLOR,
      arrowOuterMarginTop: `-${POPOVER_ARROW_BORDER_WIDTH_OUTER}`,
    },
  },
  [PopoverPosition.Bottom]: {
    popoverPosition: {
      top: POPOVER_POSITION,
    },
    arrowProps: {
      arrowPositionLeft: '50%',
      arrowPositionBottom: '100%',
      arrowInnerBorderBottomColor: POPOVER_BACKGROUND_COLOR,
      arrowInnerMarginLeft: `-${POPOVER_ARROW_BORDER_WIDTH_INNER}`,
      arrowOuterBorderBottomColor: POPOVER_BORDER_COLOR,
      arrowOuterMarginLeft: `-${POPOVER_ARROW_BORDER_WIDTH_OUTER}`,
    },
  },
  [PopoverPosition.Left]: {
    popoverPosition: {
      right: POPOVER_POSITION,
      top: '50%',
      transform: 'translateY(-50%)',
    },
    arrowProps: {
      arrowPositionTop: '50%',
      arrowPositionLeft: '100%',
      arrowInnerBorderLeftColor: POPOVER_BACKGROUND_COLOR,
      arrowInnerMarginTop: `-${POPOVER_ARROW_BORDER_WIDTH_INNER}`,
      arrowOuterBorderLeftColor: POPOVER_BORDER_COLOR,
      arrowOuterMarginTop: `-${POPOVER_ARROW_BORDER_WIDTH_OUTER}`,
    },
  },
};

const arrowPositionConfig: Config = {
  arrowPositionTop: { property: 'top' },
  arrowPositionRight: { property: 'right' },
  arrowPositionBottom: { property: 'bottom' },
  arrowPositionLeft: { property: 'left' },
};

const arrowInnerBorderColorConfig: Config = {
  arrowInnerBorderTopColor: { property: 'borderTopColor', scale: 'colors' },
  arrowInnerBorderRightColor: { property: 'borderRightColor', scale: 'colors' },
  arrowInnerBorderBottomColor: { property: 'borderBottomColor', scale: 'colors' },
  arrowInnerBorderLeftColor: { property: 'borderLeftColor', scale: 'colors' },
};

const arrowInnerMarginConfig: Config = {
  arrowInnerMarginLeft: { property: 'marginLeft' },
  arrowInnerMarginTop: { property: 'marginTop' },
};

const arrowInnerBorderColor = system(arrowInnerBorderColorConfig);
const arrowInnerMargin = system(arrowInnerMarginConfig);
export const arrowInner = compose(arrowInnerBorderColor, arrowInnerMargin);

const arrowOuterBorderColorConfig: Config = {
  arrowOuterBorderTopColor: { property: 'borderTopColor', scale: 'colors' },
  arrowOuterBorderRightColor: { property: 'borderRightColor', scale: 'colors' },
  arrowOuterBorderBottomColor: { property: 'borderBottomColor', scale: 'colors' },
  arrowOuterBorderLeftColor: { property: 'borderLeftColor', scale: 'colors' },
};

const arrowOuterMarginConfig: Config = {
  arrowOuterMarginLeft: { property: 'marginLeft' },
  arrowOuterMarginTop: { property: 'marginTop' },
};

const arrowOuterBorderColor = system(arrowOuterBorderColorConfig);
const arrowOuterMargin = system(arrowOuterMarginConfig);
export const arrowOuter = compose(arrowOuterBorderColor, arrowOuterMargin);

export const arrowPosition = system(arrowPositionConfig);
